import { LawSearchResult, LawsListResponse, LawDetailsResponse, SearchFilters } from '../types';

const API_BASE_URL = 'https://ai.isdud.com/backend/api/v1';
const API_V2_BASE_URL = 'https://ai.isdud.com/backend/api/v2';
const LIVEKIT_WSS_URL = 'wss://ai.isdud.com/livekit';

class ApiService {
  // --- المساعدات (Helpers) ---
  
  private getAuthToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  private createHeaders(includeAuth = true): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    if (includeAuth) {
      const token = this.getAuthToken();
      console.log('Token retrieved from localStorage:', token ? 'exists' : 'none');
      if (!token) {
        console.error('No authentication token found. Please login again.');
        throw new Error('Authentication token not found. Please login again.');
      }
      headers['Authorization'] = `Bearer ${token}`;
      console.log('Authorization header created with token length:', token.length);
    }
    
    return headers;
  }
// Inside your ApiService class in api.ts
async getConversationMessages(conversationId: string) {
  const token = this.getAuthToken();
  // Ensure we use the exact room name format used in the backend
  const params = new URLSearchParams({ 
    room: `room_${conversationId}` 
  });
  
  // Using the protected base URL as history requires authentication
  const response = await fetch(`${API_V2_BASE_URL}/livekit/history?${params}`, {
    method: 'GET',
    headers: this.createHeaders(!!token),
  });

  if (!response.ok) {
    if (response.status === 404) return []; // No history yet
    throw new Error('فشل في جلب سجل المحادثة');
  }

  return response.json();
}
  // --- إدارة القوانين (Law Management) ---

  async getLaws(countryCode?: string, limit = 100): Promise<LawsListResponse> {
    const params = new URLSearchParams();
    if (countryCode && countryCode !== 'all') {
      params.append('country_code', countryCode);
    }
    params.append('limit', limit.toString());

    const response = await fetch(`${API_BASE_URL}/laws?${params.toString()}`, {
      headers: this.createHeaders(false),
    });

    if (!response.ok) throw new Error('فشل جلب قائمة القوانين');
    const data = await response.json();
    
    return {
      success: true,
      documents: data.documents || [],
      total: data.total || 0
    };
  }

  async getLawByID(id: string): Promise<LawDetailsResponse> {
    const response = await fetch(`${API_BASE_URL}/laws/${id}`, {
      headers: this.createHeaders(false),
    });

    if (!response.ok) throw new Error('فشل جلب تفاصيل القانون');
    return response.json();
  }

  // --- البحث الذكي (AI & Hybrid Search) ---

  async searchLaws(query: string, filters?: SearchFilters) {
    const token = this.getAuthToken();
    const params = new URLSearchParams({ 
      query, 
      ...(filters?.country_code && { country_code: filters.country_code }),
      ...(filters?.category && { category: filters.category }),
      limit: '10'
    });
    
    const baseUrl = token ? API_V2_BASE_URL : API_BASE_URL;
    const endpoint = token ? '/protected/search/hybrid' : '/search/hybrid';
    
    const response = await fetch(`${baseUrl}${endpoint}?${params}`, {
      headers: this.createHeaders(!!token),
    });

    if (!response.ok) throw new Error('فشل عملية البحث');
    return response.json();
  }

  // --- نظام المحادثة (Chat & Streaming) ---
async sendMessageToLiveKit(roomName: string, message: string) {
  const response = await fetch(`${API_V2_BASE_URL}/protected/chat/send`, {
    method: 'POST',
    headers: this.createHeaders(true),
    body: JSON.stringify({ 
      room_name: roomName, 
      message: message 
    }),
  });
  
  if (!response.ok) throw new Error('Failed to send message to AI worker');
  return response.json();
}
  async sendChatMessage(message: string, conversationId?: string): Promise<Response> {
    const response = await fetch(`${API_V2_BASE_URL}/protected/chat/stream`, {
      method: 'POST',
      headers: this.createHeaders(true),
      body: JSON.stringify({
        message,
        conversation_id: conversationId,
        sources_only: false
      }),
    });

    if (!response.ok) throw new Error('فشل إرسال الرسالة');
    return response; // نعيد الـ Response مباشرة للتعامل مع الـ Streaming (SSE)
  }

  async getConversations(conversationType?: string) {
    const url = conversationType 
      ? `${API_V2_BASE_URL}/protected/chat/conversations?conversation_type=${conversationType}`
      : `${API_V2_BASE_URL}/protected/chat/conversations`;
    const response = await fetch(url, {
      headers: this.createHeaders(true),
    });
    if (!response.ok) throw new Error('فشل جلب المحادثات');
    return response.json();
  }

  async createConversation(title = 'استشارة قانونية جديدة', conversationType = 'consultation') {
    const response = await fetch(`${API_V2_BASE_URL}/protected/chat/conversations`, {
      method: 'POST',
      headers: this.createHeaders(true),
      body: JSON.stringify({ title, conversation_type: conversationType }),
    });
    if (!response.ok) throw new Error('فشل إنشاء محادثة');
    return response.json();
  }

  async deleteConversation(id: string) {
    const response = await fetch(`${API_V2_BASE_URL}/protected/chat/conversations/${id}`, {
      method: 'DELETE',
      headers: this.createHeaders(true),
    });
    return response.ok;
  }

  // --- نظام LiveKit (Real-time) ---

  async joinLiveKitRoom(roomName: string) {
    // Ensure auth token exists before proceeding
    if (!this.getAuthToken()) {
      throw new Error('Authentication token not found. Please login again.');
    }

    const response = await fetch(`${API_V2_BASE_URL}/livekit/join?room=${encodeURIComponent(roomName)}`, {
      headers: this.createHeaders(true),
    });

    if (!response.ok) throw new Error('Failed to connect to LiveKit server');
    const data = await response.json();
    
    console.log('=== LiveKit Backend Response ===');
    console.log('Room:', roomName);
    console.log('Backend Token:', data.token);
    console.log('Backend URL:', data.url);
    console.log('================================');
    
    // Store LiveKit token securely for this session
    if (data.token) {
      this.storeLiveKitToken(data.token, roomName);
    }
    
    const result = {
      token: data.token,
      url: data.url || LIVEKIT_WSS_URL // Use backend URL or fallback to hardcoded URL
    };
    
    console.log('=== LiveKit Connection Data ===');
    console.log('Token to connect:', result.token);
    console.log('URL to connect:', result.url);
    console.log('===============================');
    
    return result;
  }

  async getLiveKitConversations(conversationType?: string) {
    const url = conversationType 
      ? `${API_V2_BASE_URL}/livekit/conversations?conversation_type=${conversationType}`
      : `${API_V2_BASE_URL}/livekit/conversations`;
    const response = await fetch(url, {
      headers: this.createHeaders(true),
    });
    if (!response.ok) throw new Error('Failed to get LiveKit conversations');
    return response.json();
  }

  // Store LiveKit token securely for session
  private storeLiveKitToken(token: string, roomName: string): void {
    try {
      const tokenData = {
        token,
        roomName,
        timestamp: Date.now(),
        expiresAt: Date.now() + (60 * 60 * 1000) // 1 hour expiry
      };
      
      localStorage.setItem('livekit_token', JSON.stringify(tokenData));
      console.log('LiveKit token stored securely for room:', roomName);
    } catch (error) {
      console.error('Error storing LiveKit token:', error);
    }
  }

  // Get stored LiveKit token
  getLiveKitToken(): { token: string; roomName: string } | null {
    try {
      const tokenData = localStorage.getItem('livekit_token');
      if (!tokenData) return null;
      
      const parsed = JSON.parse(tokenData);
      
      // Check if token has expired
      if (Date.now() > parsed.expiresAt) {
        localStorage.removeItem('livekit_token');
        console.log('LiveKit token expired, removed from storage');
        return null;
      }
      
      return {
        token: parsed.token,
        roomName: parsed.roomName
      };
    } catch (error) {
      console.error('Error retrieving LiveKit token:', error);
      localStorage.removeItem('livekit_token');
      return null;
    }
  }

  // Clear LiveKit token on logout
  clearLiveKitToken(): void {
    localStorage.removeItem('livekit_token');
    console.log('LiveKit token cleared');
  }

  // --- إدارة الملفات (File Upload) ---

  async uploadFiles(files: File[], conversationId: string) {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    formData.append('conversation_id', conversationId);

    const response = await fetch(`${API_V2_BASE_URL}/protected/chat/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.getAuthToken()}`
        // ملاحظة: لا نضع Content-Type هنا ليقوم المتصفح بضبط الـ Boundary تلقائياً
      },
      body: formData,
    });

    if (!response.ok) throw new Error('فشل رفع الملفات');
    return response.json();
  }

  // --- الملف الشخصي (User Profile) ---

  async getUserProfile() {
    const response = await fetch(`${API_V2_BASE_URL}/protected/user/profile`, {
      headers: this.createHeaders(true),
    });
    if (!response.ok) throw new Error('فشل جلب بيانات المستخدم');
    return response.json();
  }
}

export const apiService = new ApiService();